import React from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'
import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  IconInfo,
  IconWarningTriangle,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Flex,
  FlexItem,
  Title,
  TITLE_VARIANT_M,
  TITLE_VARIANT_S,
  Text,
  TEXT_VARIANT_LIGHT,
  Divider,
  SpacingVertical,
  SPACING_24,
  SPACING_32
} from 'mailup-web-components'
import {
  callbackManagerAlert,
  callbackResetManagerAlert
} from 'Pages/MessageCheckupSummary/recoil/callbacks/callbacks'

import { modalManageAlertsState } from 'Pages/MessageCheckupSummary/recoil/state/modals'
import {
  mdCheckup,
  messageState
} from 'Pages/MessageCheckupSummary/recoil/state/message'
import AlarmSwitch from 'Components/AlarmSwitch'
import { EmailMessageCheckUpIssueType } from 'Constants/'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { CUSTOM_DOMAIN_NOT_CUSTOMIZABLE } from 'Constants/index'

const ModalItem = ({
  title,
  emailMessageCheckUpIssueType,
  icon,
  isLast = false
}) => {
  const intl = useIntl()
  const [{ silencedAlertsArray }, setMdState] = useRecoilState(mdCheckup)

  const handleAlarmChange = e => {
    const value = e.toLowerCase()
    const newItems = s => {
      const resItems = []
      s.forEach(e => {
        if (e.issueType === emailMessageCheckUpIssueType) {
          // element copy
          const obj = {
            silenced: value,
            stateManaged: true,
            disableForMessage: e.disableForMessage,
            enable: e.enable,
            issueType: e.issueType
          }
          resItems.push(obj)
        } else {
          resItems.push(e)
        }
      })
      return resItems
    }
    setMdState(prev => ({
      ...prev,
      silencedAlertsArray: newItems(prev.silencedAlertsArray)
    }))
  }

  const subtitle = () => {
    const state =
      silencedAlertsArray.length > 0 &&
      silencedAlertsArray.find(
        e => e.issueType === emailMessageCheckUpIssueType
      ).silenced
    switch (state) {
      case 'enable':
        return intl.formatMessage({
          id: 'Load.EmailCheckUp.AlertEnable'
        })
      case 'silent':
        return intl.formatMessage({
          id: 'Load.EmailCheckUp.AlertSilent'
        })
      case 'disable':
        return intl.formatMessage({
          id: 'Load.EmailCheckUp.AlertDisable'
        })
    }
  }

  return (
    <>
      <Flex width='100%' alignItems='flex-start' margin='24px 0 0 0'>
        <FlexItem>
          {icon === 'info' && <StyledIconInfo />}
          {icon === 'alert' && <StyledIconWarning />}
        </FlexItem>
        <FlexItem width='100%'>
          <Title variant={TITLE_VARIANT_S}>
            {intl.formatMessage({
              id: title
            })}
          </Title>

          <Text variant={TEXT_VARIANT_LIGHT}>{subtitle()}</Text>
        </FlexItem>
        <FlexItem margin='0 24px 0 0'>
          <AlarmSwitch
            value={
              silencedAlertsArray.length > 0 &&
              silencedAlertsArray.find(
                e => e.issueType === emailMessageCheckUpIssueType
              ).silenced
            }
            onChange={handleAlarmChange}
          />
        </FlexItem>
      </Flex>
      {!isLast && (
        <>
          <SpacingVertical variant={SPACING_24} />
          <Divider />
        </>
      )}
    </>
  )
}

const ModalManageAlerts = () => {
  const intl = useIntl()
  const {
    id,
    checkup: { domainCustomizableStatus }
  } = useRecoilValue(messageState)
  const { show } = useRecoilValue(modalManageAlertsState)
  const saveManagerAlert = useRecoilCallback(callbackManagerAlert, [])
  const resetManagerAlert = useRecoilCallback(callbackResetManagerAlert, [])

  const handleClose = () => {
    // reset and close modale
    resetManagerAlert(id)
  }

  const handleSave = () => {
    // reset and close modale
    saveManagerAlert(id)
  }

  return (
    <Modal show={show} showClose closeOnOverlayClick onClose={handleClose}>
      <ModalHead
        title={intl.formatMessage({
          id: 'Load.EmailCheckUp.AlertModalTitle'
        })}
      />
      <ModalBody scrollable>
        <Flex flexDirection='column' width='100%' maxHeight='335px'>
          <FlexItem>
            <Title variant={TITLE_VARIANT_M}>
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.Critique'
              })}
            </Title>
            <ModalItem
              title='Load.EmailCheckUp.BlackListUrlWarning'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.BlackListUrlWarning
              }
              icon='alert'
            />
            <ModalItem
              title='Load.EmailCheckUp.LinkTrackingWarning'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.LinkTrackingWarning
              }
              icon='alert'
            />
            <ModalItem
              title='Load.EmailCheckUp.UnsubscribeLinkWarning'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.UnsubscribeLinkWarning
              }
              icon='alert'
            />
            <ModalItem
              title='Load.EmailCheckUp.DynamicFields'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.DynamicFieldsWarning
              }
              icon='alert'
            />
            <ModalItem
              title='Load.EmailCheckUp.WorkflowWarning'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.WorkflowWarning
              }
              icon='alert'
            />
            <ModalItem
              title='Load.EmailCheckUp.MaxSizeInfo'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.MaxSizeInfo
              }
              icon='alert'
              isLast
            />
          </FlexItem>

          <FlexItem>
            <SpacingVertical variant={SPACING_32} />
            <Title variant={TITLE_VARIANT_M}>
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.BestPractice'
              })}
            </Title>
            <ModalItem
              title='Load.EmailCheckUp.PreheaderInfo'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.PreheaderInfo
              }
              icon='info'
            />
            <ModalItem
              title='Load.EmailCheckUp.WebAnalyticsTrackingInfo'
              emailMessageCheckUpIssueType={
                EmailMessageCheckUpIssueType.WebAnalyticsTrackingInfo
              }
              icon='info'
            />
            {domainCustomizableStatus &&
              domainCustomizableStatus !== CUSTOM_DOMAIN_NOT_CUSTOMIZABLE && (
                <ModalItem
                  title='Load.EmailCheckUp.CustomTrackingDomainKo'
                  emailMessageCheckUpIssueType={
                    EmailMessageCheckUpIssueType.DomainCustomizable
                  }
                  icon='info'
                  isLast
                />
              )}
          </FlexItem>
        </Flex>
      </ModalBody>
      <ModalFoot>
        <Button kind={BUTTON_VARIANT_SECONDARY} onClick={handleClose}>
          {intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        </Button>
        <Button kind={BUTTON_VARIANT_PRIMARY} onClick={handleSave}>
          {intl.formatMessage({ id: 'Load.MailupCommon.Save' })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalManageAlerts

const StyledIconInfo = styled(IconInfo)`
  width: 35px;
  color: var(--java);
  font-size: 18px;
`
const StyledIconWarning = styled(IconWarningTriangle)`
  width: 35px;
  color: var(--sun);
  font-size: 18px;
`

ModalItem.propTypes = {
  title: PropTypes.string,
  emailMessageCheckUpIssueType: PropTypes.string,
  icon: PropTypes.string,
  isLast: PropTypes.bool
}
